import * as React from 'react';
import { useRecoilValue } from 'recoil';

import { useSurvey } from '@src/hooks/useSurvey';
import { userAtom } from '@src/recoil/atoms/user';
import { trimCharacters } from '@src/utils/strings';
import { SurveyQuestionProps } from './SurveyQuestion';

export interface TypeTextProps extends SurveyQuestionProps {}

/**
 * @name TypeText
 * @description Collect some text information from the survey
 */
export const TypeText = (props: TypeTextProps) => {
  const { question } = props;

  // Hooks
  const ref = React.useRef<HTMLInputElement>(null);
  const { answers, setAnswer } = useSurvey();
  const value = answers[question.name];
  const { email, name } = useRecoilValue(userAtom);

  // Setup
  const { error_message, placeholder, properties = {}, required = false } = question; // prettier-ignore
  const { autocomplete, pattern } = properties;

  // Detect login credentials
  React.useEffect(() => {
    if (!ref.current || !email) return;

    const nameArray = name?.trim()?.split(' ') ?? [''];
    const firstName = nameArray[0] || 'First';
    const lastName = nameArray[nameArray.length - 1] || 'Last';

    let value = '';
    if (question.id === 'first_name') {
      value = firstName;
    } else if (question.id === 'last_name') {
      value = lastName;
    }

    if (!value) return;

    ref.current.value = value;
    ref.current.disabled = true;

    setAnswer(question.name, value);
  }, [email, question.name]);

  // Handlers
  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.currentTarget.setCustomValidity('');
    event.preventDefault();

    const value = event.target.value !== '' ? event.target.value : undefined;
    const answer = value ? trimCharacters(value, question.name) : value;

    setAnswer(question.name, answer);
  };

  return (
    <input
      ref={ref}
      autoComplete={autocomplete}
      className="m-0 rounded-none border-0 p-3 font-beatrice-light text-14 md:px-8 md:pt-4 md:text-18"
      defaultValue={value}
      name={question.name}
      onChange={onChangeHandler}
      onInvalid={(event: React.FormEvent<HTMLInputElement>) => {
        if (error_message) event.currentTarget.setCustomValidity(error_message);
      }}
      pattern={pattern}
      placeholder={placeholder ?? ''}
      required={required}
      type="text"
    />
  );
};
